import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardPage } from '../../components/PremiumDesign';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';

import {
  RemindersSummaryCards,
  RemindersFilterToolbar,
  RemindersTable,
  ReminderDetailModal,
  ReminderActionModals,
  ReminderImportChannelModal,
} from '../components';
import {
  useReminders,
  useRemindersSummary,
  useReminderFilters,
  useReminderFollowUpActions,
} from '../hooks';
import { getCompanyId } from '../utils';
import { useMessagingChannels } from '../../hooks/useMessagingChannels';

export function RemindersPage() {
  const navigate = useNavigate();
  const companyId = getCompanyId();
  const { can } = usePermissions();
  const canImport = can(PERMISSIONS.REMINDERS.CREATE);
  const canUpdate = can(PERMISSIONS.REMINDERS.UPDATE);
  const canOpenSettings = can(PERMISSIONS.SETTINGS.VIEW);
  const channels = useMessagingChannels(companyId);
  const [selectedReminderId, setSelectedReminderId] = React.useState(null);
  const [actionPopup, setActionPopup] = React.useState(null);

  const [isImportModalOpen, setIsImportModalOpen] = React.useState(false);

  const {
    page,
    setPage,
    pageSize,
    search,
    setSearch,
    typeFilter,
    setTypeFilter,
    statusFilter,
    setStatusFilter,
    assignedEmployeeId,
    setAssignedEmployeeId,
    dateFrom,
    dateTo,
    setDateRange,
    sortKey,
    setSortKey,
    activeFilterFields,
    contactId,
    filters,
    handleAddFilterField,
    handleRemoveFilterField,
    clearAllFilters,
    handleSummaryClick,
  } = useReminderFilters();

  const { data: listData, isLoading: listLoading } = useReminders(
    companyId,
    filters,
  );
  const { data: summary, isLoading: summaryLoading } = useRemindersSummary(
    companyId,
    { contactId, assignedEmployeeId },
  );
  const followUpActions = useReminderFollowUpActions(companyId, {
    onDone: () => setActionPopup(null),
  });

  const reminders = listData?.results || [];
  const totalCount = listData?.count || 0;
  const showSkeleton = useDelayedSkeleton(listLoading && !listData);

  return (
    <DashboardPage className="reminders-page !p-3 sm:!p-4 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <RemindersSummaryCards
        summary={summary}
        isLoading={summaryLoading}
        onCardClick={handleSummaryClick}
      />

      <RemindersFilterToolbar
        companyId={companyId}
        search={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        assignedEmployeeId={assignedEmployeeId}
        onAssignedEmployeeChange={setAssignedEmployeeId}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateRangeChange={setDateRange}
        sortKey={sortKey}
        onSortKeyChange={setSortKey}
        activeFilterFields={activeFilterFields}
        onAddFilterField={handleAddFilterField}
        onRemoveFilterField={handleRemoveFilterField}
        onClearAllFilters={clearAllFilters}
        canImport={canImport}
        onImportClick={() => {
          if (
            channels.isReady &&
            channels.email !== channels.whatsapp &&
            (channels.email || channels.whatsapp)
          ) {
            navigate(
              `/reminders/import?channel=${channels.email ? 'email' : 'whatsapp'}`,
            );
            return;
          }
          setIsImportModalOpen(true);
        }}
      />

      <RemindersTable
        reminders={reminders}
        isLoading={showSkeleton}
        page={page}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
        onRowClick={(reminder) => setSelectedReminderId(reminder.id)}
        onAction={setActionPopup}
        canUpdate={canUpdate}
        actionsDisabled={followUpActions.isBusy}
      />

      <ReminderDetailModal
        companyId={companyId}
        reminderId={selectedReminderId}
        onClose={() => setSelectedReminderId(null)}
        onAction={setActionPopup}
        canUpdate={canUpdate}
        isBusy={followUpActions.isBusy}
      />

      <ReminderActionModals
        actionPopup={actionPopup}
        onClose={() => setActionPopup(null)}
        stop={followUpActions.stop}
        pause={followUpActions.pause}
        resume={followUpActions.resume}
        reschedule={followUpActions.reschedule}
        isStopping={followUpActions.isStopping}
        isPausing={followUpActions.isPausing}
        isResuming={followUpActions.isResuming}
        isRescheduling={followUpActions.isRescheduling}
      />

      <ReminderImportChannelModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        status={channels}
        canOpenSettings={canOpenSettings}
        onSelectChannel={(channel) => {
          if (!channels.isReady) return;
          if (channel === 'email' && !channels.email) return;
          if (channel === 'whatsapp' && !channels.whatsapp) return;
          navigate(`/reminders/import?channel=${channel}`);
        }}
      />
    </DashboardPage>
  );
}
